import React, { useEffect, useState, useCallback } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { SecondaryBase } from '../Commons/SecondaryBase';
import { useAuth } from '../../Providers/AuthProvider';
import { useFloatMessage, MESSAGE_TYPE } from '../../Providers/FloatMessage';
import { ResetCurrentDynamic as BLResetCurrentDynamic } from '../../../utilities/BL/Zones';

import {
    GetThemesBL,
    DeleteThemeBL,
    CloneThemeBL,
    GetMatchHistoryBL,
    CreateLiveMatchBL,
    StartLiveMatchBL,
    PauseLiveMatchBL,
    ResumeLiveMatchBL,
    FinishLiveMatchEarlyBL,
    ResetLiveMatchBL,
    GetLiveMatchStateBL,
    ShowCarreraContinuaCoverBL,
    ShowCarreraContinuaGameBL
} from '../../../utilities/BL/CarreraContinua';

import {
    ConnectWebSocketForCarreraContinuaAdmin,
    DisconnectWebSocketForCarreraContinuaAdmin
} from '../../../utilities/WS/Admin/CarreraContinuaWS';

import { CarreraContinuaThemeModal } from './CarreraContinua/CarreraContinuaThemeModal';
import { Confirm } from '../../Commons/Modals/Confirm';

// Helper para resolver URL de imágenes completas
const getFullAssetUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
        return url;
    }
    const apiUrl = process.env.REACT_APP_API_URL || 'https://localhost:50586/api/';
    const apiRoot = apiUrl.replace(/\/api\/?$/, '');
    return `${apiRoot}${url.startsWith('/') ? '' : '/'}${url}`;
};

const CarreraContinua = ({ getOutOfTheDynamic, roomId: propRoomId }) => {
    const { user } = useAuth();
    const { showMessage } = useFloatMessage();

    // =========================================================================
    // IDENTIFICADOR DE SALA Y LISTADO DE SALAS ACTIVAS
    // =========================================================================
    const searchParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
    const initialRoomId = propRoomId || searchParams?.get('roomId') || '';

    const [activeRooms, setActiveRooms] = useState([]);
    const [selectedRoomId, setSelectedRoomId] = useState(initialRoomId);

    // Cargar listado de salas activas desde la API
    useEffect(() => {
        const fetchActiveRooms = async () => {
            try {
                const apiBase = process.env.REACT_APP_API_URL || 'https://localhost:50586/api/';
                const res = await fetch(`${apiBase}v2/rooms/active`);
                if (res.ok) {
                    const data = await res.json();
                    if (Array.isArray(data) && data.length > 0) {
                        setActiveRooms(data);
                        setSelectedRoomId((prev) => {
                            if (prev && data.some(r => String(r.id).toLowerCase() === String(prev).toLowerCase())) {
                                return prev;
                            }
                            const defaultRoom = data.find(r => r.isDefault) || data[0];
                            return defaultRoom ? String(defaultRoom.id) : '';
                        });
                    }
                }
            } catch (err) {
                console.warn('[CarreraContinua] Error al consultar salas activas:', err);
            }
        };
        fetchActiveRooms();
    }, [propRoomId]);

    const roomId = selectedRoomId || propRoomId || searchParams?.get('roomId') || null;

    const playerUrl = typeof window !== 'undefined'
        ? (roomId ? `${window.location.origin}/?roomId=${roomId}` : `${window.location.origin}/`)
        : (roomId ? `/?roomId=${roomId}` : `/`);

    // =========================================================================
    // ESTADOS DE RED Y WEBSOCKET
    // =========================================================================
    const [wsStatus, setWsStatus] = useState('idle');
    const [isBusy, setIsBusy] = useState(false);

    // =========================================================================
    // MODAL DE CONFIRMACIÓN (REINICIAR PARTIDA)
    // =========================================================================
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmModalData, setConfirmModalData] = useState(null);

    const openConfirmModal = (message, action, yesText = 'Sí, reiniciar') => {
        setConfirmModalData({
            message,
            actionToYesButton: action,
            textYesButton: yesText,
            textNoButton: 'Cancelar'
        });
        setShowConfirmModal(true);
    };

    // =========================================================================
    // TARJETA 1: CONFIGURACIÓN DE PARTIDA Y TEMAS
    // =========================================================================
    const [themesList, setThemesList] = useState([]);
    const [selectedThemeId, setSelectedThemeId] = useState('');
    const [durationSeconds, setDurationSeconds] = useState(45);
    const [totalKilometers, setTotalKilometers] = useState(5);
    const [enableFoamArches, setEnableFoamArches] = useState(true);

    // =========================================================================
    // MODAL DE GESTIÓN DE TEMAS (HU-04)
    // =========================================================================
    const [showThemeModal, setShowThemeModal] = useState(false);
    const [themeToEdit, setThemeToEdit] = useState(null);

    // =========================================================================
    // TARJETA 3: TELEMETRÍA EN VIVO Y ESTADOS DE CARRERA
    // =========================================================================
    const [matchPhase, setMatchPhase] = useState('WAITING'); // WAITING, COUNTDOWN, RUNNING, PAUSED, FINISHED
    const [countdownNumber, setCountdownNumber] = useState(null);
    const [liveTelemetry, setLiveTelemetry] = useState(null);
    const [winnerResult, setWinnerResult] = useState(null);

    // =========================================================================
    // TARJETA 5: HISTORIAL DE PARTIDAS
    // =========================================================================
    const [matchHistory, setMatchHistory] = useState([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);

    // =========================================================================
    // MODAL QR
    // =========================================================================
    const [showQRModal, setShowQRModal] = useState(false);
    const [isCopied, setIsCopied] = useState(false);

    // =========================================================================
    // HELPERS DE COLOR POR ZONA
    // =========================================================================
    const getZoneColor = (zoneIdentifier) => {
        const key = (zoneIdentifier || '').toLowerCase();
        if (key.includes('norte') || key.includes('north') || key.includes('azul') || key.includes('blue') || key === '1') return '#0d6efd';
        if (key.includes('sur') || key.includes('south') || key.includes('rojo') || key.includes('red') || key === '2') return '#dc3545';
        if (key.includes('oriente') || key.includes('east') || key.includes('verde') || key.includes('green') || key === '3') return '#198754';
        if (key.includes('poniente') || key.includes('west') || key.includes('amarillo') || key.includes('yellow') || key === '4') return '#ffc107';
        if (key.includes('morado') || key.includes('purple')) return '#6f42c1';
        return '#0dcaf0';
    };

    // =========================================================================
    // CARGAR TEMAS E HISTORIAL
    // =========================================================================
    const loadThemes = useCallback(async () => {
        try {
            const res = await GetThemesBL(roomId, user?.id);
            if (res.success && res.data && res.data.length > 0) {
                setThemesList(res.data);
                // Si el tema seleccionado ya no existe o no está definido, seleccionar el primero
                setSelectedThemeId((prev) => {
                    if (prev && res.data.some((t) => String(t.id) === String(prev))) {
                        return prev;
                    }
                    const first = res.data[0];
                    if (first.totalKilometers) setTotalKilometers(first.totalKilometers);
                    if (first.enableFoamArches !== undefined) setEnableFoamArches(first.enableFoamArches);
                    return String(first.id);
                });
            } else {
                setThemesList([]);
            }
        } catch (err) {
            console.error('[CarreraContinua] Error al cargar temas:', err);
        }
    }, [roomId, user?.id]);

    const loadHistory = useCallback(async () => {
        setIsLoadingHistory(true);
        try {
            const res = await GetMatchHistoryBL(roomId, 15);
            if (res.success && res.data) {
                setMatchHistory(res.data);
            }
        } catch (err) {
            console.error('[CarreraContinua] Error al cargar historial:', err);
        } finally {
            setIsLoadingHistory(false);
        }
    }, [roomId]);

    const checkLiveState = useCallback(async () => {
        try {
            const res = await GetLiveMatchStateBL(roomId);
            if (res.success && res.data) {
                const s = res.data;
                setMatchPhase((s.phase || 'WAITING').toUpperCase());
                setLiveTelemetry({
                    elapsedMilliseconds: s.elapsedMilliseconds,
                    totalDurationMilliseconds: s.totalDurationMilliseconds,
                    progressPercentage: s.progressPercentage,
                    currentLeadingKilometer: s.currentLeadingKilometer,
                    zonesProgress: s.zonesProgress || []
                });
                if (s.themeId) setSelectedThemeId(String(s.themeId));
                if (s.durationSeconds) setDurationSeconds(s.durationSeconds);
                if (s.totalKilometers) setTotalKilometers(s.totalKilometers);
            }
        } catch (err) {
            // No hay partida en curso o sala nueva
        }
    }, [roomId]);

    // =========================================================================
    // ESCUCHADOR DE SIGNALR
    // =========================================================================
    const handleIncomingMessage = useCallback((envelope) => {
        if (!envelope || !envelope.type) return;

        const type = envelope.type.toLowerCase();
        const payload = envelope.payload !== undefined ? envelope.payload : (envelope.data || {});

        switch (type) {
            case 'carreracontinua.created':
                setMatchPhase('WAITING');
                setCountdownNumber(null);
                setWinnerResult(null);
                setLiveTelemetry(null);
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'Nueva partida de Carrera Continua preparada.' });
                break;

            case 'carreracontinua.countdown':
                setMatchPhase('COUNTDOWN');
                setCountdownNumber(payload.secondsRemaining);
                break;

            case 'carreracontinua.started':
                setMatchPhase('RUNNING');
                setCountdownNumber(null);
                setWinnerResult(null);
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: '¡Arrancó la Carrera Continua!' });
                break;

            case 'carreracontinua.progress':
                if (payload.phase) {
                    setMatchPhase(payload.phase.toUpperCase());
                }
                setLiveTelemetry(payload);
                break;

            case 'carreracontinua.arch_triggered':
                showMessage({
                    type: MESSAGE_TYPE.INFO,
                    text: `🏁 ¡Hito superado! ${payload.label || `Km ${payload.kilometer}`} cruzado.`
                });
                break;

            case 'carreracontinua.paused':
                setMatchPhase('PAUSED');
                showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Carrera pausada.' });
                break;

            case 'carreracontinua.resumed':
                setMatchPhase('RUNNING');
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Carrera reanudada.' });
                break;

            case 'carreracontinua.finished':
                setMatchPhase('FINISHED');
                setWinnerResult(payload);
                loadHistory();
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: '🏁 ¡Carrera finalizada con éxito!' });
                break;

            case 'carreracontinua.reset':
                setMatchPhase('WAITING');
                setCountdownNumber(null);
                setLiveTelemetry(null);
                setWinnerResult(null);
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'Carrera reiniciada a estado de espera.' });
                break;

            default:
                break;
        }
    }, [loadHistory, showMessage]);

    // Inicialización de datos
    useEffect(() => {
        loadThemes();
        loadHistory();
        checkLiveState();
    }, [roomId]);

    // Conexión WebSocket persistente de sala
    useEffect(() => {
        ConnectWebSocketForCarreraContinuaAdmin(handleIncomingMessage, setWsStatus, roomId);

        return () => {
            DisconnectWebSocketForCarreraContinuaAdmin();
        };
    }, [roomId]);

    // =========================================================================
    // ACCIONES DE GESTIÓN DE TEMAS (HU-04)
    // =========================================================================
    const handleOpenCreateTheme = () => {
        setThemeToEdit(null);
        setShowThemeModal(true);
    };

    const handleOpenEditTheme = (theme) => {
        setThemeToEdit(theme);
        setShowThemeModal(true);
    };

    const handleCloneTheme = async (theme) => {
        if (!theme || !theme.id) return;
        setIsBusy(true);
        try {
            const res = await CloneThemeBL(theme.id, `${theme.name} (Copia)`);
            if (res.success && res.data) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: `Tema clonado exitosamente: "${res.data.name}".` });
                await loadThemes();
                setSelectedThemeId(String(res.data.id));
                if (res.data.totalKilometers) setTotalKilometers(res.data.totalKilometers);
                if (res.data.enableFoamArches !== undefined) setEnableFoamArches(res.data.enableFoamArches);
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al clonar el tema.' });
            }
        } catch (err) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error inesperado al clonar el tema.' });
        } finally {
            setIsBusy(false);
        }
    };

    const handleDeleteTheme = async (theme) => {
        if (!theme || !theme.id) return;
        if (theme.isSystemPreset) {
            showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Los temas predeterminados del sistema no se pueden eliminar.' });
            return;
        }
        if (!window.confirm(`¿Estás seguro de que deseas eliminar permanentemente el tema "${theme.name}"?`)) {
            return;
        }

        setIsBusy(true);
        try {
            const res = await DeleteThemeBL(theme.id);
            if (res.success) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Tema eliminado exitosamente.' });
                await loadThemes();
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al eliminar el tema.' });
            }
        } catch (err) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error inesperado al eliminar el tema.' });
        } finally {
            setIsBusy(false);
        }
    };

    const handleThemeSaved = async (savedTheme) => {
        await loadThemes();
        if (savedTheme && savedTheme.id) {
            setSelectedThemeId(String(savedTheme.id));
            if (savedTheme.totalKilometers) setTotalKilometers(savedTheme.totalKilometers);
            if (savedTheme.enableFoamArches !== undefined) setEnableFoamArches(savedTheme.enableFoamArches);
        }
    };

    // =========================================================================
    // ACCIONES: TARJETA 1 & 2 (CONFIGURACIÓN, PROYECCIÓN Y ARRANQUE)
    // =========================================================================
    const handleThemeSelectionChange = (themeId) => {
        setSelectedThemeId(themeId);
        const found = themesList.find((t) => String(t.id) === String(themeId));
        if (found) {
            if (found.totalKilometers) setTotalKilometers(found.totalKilometers);
            if (found.enableFoamArches !== undefined) setEnableFoamArches(found.enableFoamArches);
            showMessage({ type: MESSAGE_TYPE.INFO, text: `Tema activo seleccionado: "${found.name}".` });
        }
    };

    const handlePrepareMatch = async () => {
        if (!selectedThemeId) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Por favor, selecciona un tema antes de continuar.' });
            return;
        }

        setIsBusy(true);
        try {
            const res = await CreateLiveMatchBL({
                roomId,
                themeId: Number(selectedThemeId),
                durationSeconds: Number(durationSeconds),
                totalKilometers: Number(totalKilometers)
            });

            if (res.success) {
                setMatchPhase('WAITING');
                setWinnerResult(null);
                setLiveTelemetry(null);
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Partida configurada y lista para iniciar.' });
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al preparar la partida.' });
            }
        } catch (err) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error inesperado al preparar la partida.' });
        } finally {
            setIsBusy(false);
        }
    };

    const handleShowCover = async () => {
        setIsBusy(true);
        try {
            await ResetLiveMatchBL(roomId).catch(() => {});
            setMatchPhase('WAITING');
            setCountdownNumber(null);
            setWinnerResult(null);
            setLiveTelemetry(null);
            if (selectedThemeId) {
                await CreateLiveMatchBL({
                    roomId,
                    themeId: Number(selectedThemeId),
                    durationSeconds: Number(durationSeconds),
                    totalKilometers: Number(totalKilometers)
                }).catch(() => {});
            }

            const res = await ShowCarreraContinuaCoverBL(roomId, selectedThemeId ? Number(selectedThemeId) : null, 1);
            if (res.success) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Portada de Carrera Continua proyectada.' });
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al proyectar la portada.' });
            }
        } catch (err) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al enviar señal de portada.' });
        } finally {
            setIsBusy(false);
        }
    };

    const handleProjectGame = async () => {
        setIsBusy(true);
        try {
            await ResetLiveMatchBL(roomId).catch(() => {});
            setMatchPhase('WAITING');
            setCountdownNumber(null);
            setWinnerResult(null);
            if (selectedThemeId) {
                await CreateLiveMatchBL({
                    roomId,
                    themeId: Number(selectedThemeId),
                    durationSeconds: Number(durationSeconds),
                    totalKilometers: Number(totalKilometers)
                }).catch(() => {});
            }

            const res = await ShowCarreraContinuaGameBL(roomId, selectedThemeId ? Number(selectedThemeId) : null, 1);
            if (res.success) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Pantalla de juego proyectada en el lienzo.' });
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al proyectar el juego.' });
            }
        } catch (err) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al enviar señal de juego.' });
        } finally {
            setIsBusy(false);
        }
    };

    const handleStartMatch = async () => {
        if (!selectedThemeId) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Selecciona un tema primero.' });
            return;
        }

        setIsBusy(true);
        try {
            // Aseguramos que la partida esté creada primero
            await CreateLiveMatchBL({
                roomId,
                themeId: Number(selectedThemeId),
                durationSeconds: Number(durationSeconds),
                totalKilometers: Number(totalKilometers)
            });

            const res = await StartLiveMatchBL(roomId);
            if (!res.success) {
                if (res.message === 'NO_PLAYERS_CONNECTED' || res.error?.includes('jugadores')) {
                    showMessage({
                        type: MESSAGE_TYPE.BAD,
                        text: '⚠️ No hay jugadores conectados en la sala. Abre el modal QR para que se unan.'
                    });
                } else {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al iniciar la carrera.' });
                }
            } else {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Iniciando cuenta regresiva...' });
            }
        } catch (err) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error inesperado al arrancar la carrera.' });
        } finally {
            setIsBusy(false);
        }
    };

    // =========================================================================
    // ACCIONES: TARJETA 4 (CONTROLES DE EMERGENCIA)
    // =========================================================================
    const handlePauseMatch = async () => {
        setIsBusy(true);
        try {
            const res = await PauseLiveMatchBL(roomId);
            if (res.success) {
                setMatchPhase('PAUSED');
                showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Carrera pausada.' });
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al pausar.' });
            }
        } catch (err) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al pausar carrera.' });
        } finally {
            setIsBusy(false);
        }
    };

    const handleResumeMatch = async () => {
        setIsBusy(true);
        try {
            const res = await ResumeLiveMatchBL(roomId);
            if (res.success) {
                setMatchPhase('RUNNING');
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Carrera reanudada.' });
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al reanudar.' });
            }
        } catch (err) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al reanudar carrera.' });
        } finally {
            setIsBusy(false);
        }
    };

    const handleFinishEarly = async () => {
        if (!window.confirm('¿Estás seguro de finalizar la carrera anticipadamente? Se calcularán los resultados actuales.')) {
            return;
        }

        setIsBusy(true);
        try {
            const res = await FinishLiveMatchEarlyBL(roomId);
            if (res.success) {
                setMatchPhase('FINISHED');
                loadHistory();
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Carrera finalizada anticipadamente.' });
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al finalizar.' });
            }
        } catch (err) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al finalizar carrera.' });
        } finally {
            setIsBusy(false);
        }
    };

    const handleResetMatch = async () => {
        setIsBusy(true);
        try {
            const res = await ResetLiveMatchBL(roomId);
            if (res.success) {
                setMatchPhase('WAITING');
                setCountdownNumber(null);
                setLiveTelemetry(null);
                setWinnerResult(null);
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'Carrera reiniciada.' });
                return { success: true };
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al reiniciar.' });
                return { success: false, message: res.message || 'Error al reiniciar.' };
            }
        } catch (err) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al reiniciar carrera.' });
            return { success: false, message: 'Error al reiniciar carrera.' };
        } finally {
            setIsBusy(false);
        }
    };

    // =========================================================================
    // SALIDA AL DASHBOARD
    // =========================================================================
    const handleReturnToDashboard = async () => {
        try {
            await BLResetCurrentDynamic();
        } catch (e) { }
        getOutOfTheDynamic();
    };

    // Copiar URL al portapapeles
    const handleCopyUrl = () => {
        if (navigator?.clipboard?.writeText) {
            navigator.clipboard.writeText(playerUrl);
            setIsCopied(true);
            setTimeout(() => setIsCopied(false), 2500);
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Enlace copiado al portapapeles.' });
        }
    };

    return (
        <SecondaryBase actionToExecute={handleReturnToDashboard}>
            <div className="container-fluid pb-5">
                
                {/* ENCABEZADO DE CONTROL */}
                <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 p-3">
                    <div className="d-flex align-items-center">
                        <i className="bi bi-person-walking fs-1 text-primary me-3"></i>
                        <div>
                            <h3 className="fw-bold mb-0 text-dark">Carrera continua</h3>
                        </div>
                    </div>

                    <div className="d-flex align-items-center gap-3 mt-3 mt-md-0">
                        {/* <div>
                            <span className={`badge ${wsStatus === 'connected' ? 'bg-success' : wsStatus === 'connecting' ? 'bg-warning text-dark' : 'bg-danger'}`}>
                                <i className={`bi ${wsStatus === 'connected' ? 'bi-wifi' : 'bi-wifi-off'} me-1`}></i>
                                {wsStatus === 'connected' ? 'En línea' : wsStatus === 'connecting' ? 'Conectando...' : 'Desconectado'}
                            </span>
                        </div> */}

                        <div>
                            <span className={`badge px-3 py-2 fs-6 ${
                                matchPhase === 'RUNNING' ? 'bg-success' :
                                matchPhase === 'COUNTDOWN' ? 'bg-warning text-dark' :
                                matchPhase === 'PAUSED' ? 'bg-secondary' :
                                matchPhase === 'FINISHED' ? 'bg-primary' : 'bg-dark'
                            }`}>
                                {matchPhase === 'WAITING' && '⏳ En Espera'}
                                {matchPhase === 'COUNTDOWN' && '🚦 Cuenta Regresiva'}
                                {matchPhase === 'RUNNING' && '🏃 ¡En Carrera!'}
                                {matchPhase === 'PAUSED' && '⏸️ Pausada'}
                                {matchPhase === 'FINISHED' && '🏁 Finalizada'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* FILA 1: TARJETA 1 (CONFIGURACIÓN) Y TARJETA 2 (PROYECCIÓN Y ENLACES) */}
                <div className="row g-4 mb-4">
                    
                    {/* TARJETA 1: CONFIGURACIÓN DE PARTIDA */}
                    <div className="col-12 col-lg-6">
                        <div className="card h-100 shadow-sm border-0 rounded-3">
                            <div className="card-header bg-dark text-white d-flex justify-content-between align-items-center py-3">
                                <h5 className="mb-0 fw-bold">
                                    <i className="bi bi-gear-fill me-2 text-warning"></i>
                                    1. Configuración de Partida
                                </h5>
                                <button
                                    className="btn btn-sm btn-outline-light"
                                    onClick={loadThemes}
                                    title="Recargar temas"
                                    disabled={isBusy}
                                >
                                    <i className="bi bi-arrow-clockwise"></i>
                                </button>
                            </div>
                            <div className="card-body bg-light">
                                
                                {/* SELECTOR DE SALA ASIGNADA */}
                                <div className="mb-3 p-2 bg-white rounded border">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <label className="form-label fw-bold text-primary small text-uppercase mb-0">
                                            <i className="bi bi-door-open-fill me-1"></i> Sala Asignada (SaaS / Estadio):
                                        </label>
                                        {roomId && (
                                            <span className="badge bg-primary-subtle text-primary border border-primary-subtle small">
                                                Activa
                                            </span>
                                        )}
                                    </div>
                                    <select
                                        className="form-select form-select-sm fw-semibold"
                                        value={roomId || ''}
                                        onChange={(e) => setSelectedRoomId(e.target.value)}
                                        disabled={isBusy || matchPhase === 'RUNNING' || matchPhase === 'COUNTDOWN'}
                                    >
                                        {activeRooms.map((r) => (
                                            <option key={r.id} value={r.id}>
                                                {r.nombreSala} {r.isDefault ? ' ⭐ (Por Defecto)' : ''}
                                            </option>
                                        ))}
                                        <option value="">-- Modo Estadio General (Sin Sala) --</option>
                                    </select>
                                </div>

                                {/* SELECTOR DE TEMA */}
                                <div className="mb-3">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <label className="form-label fw-bold text-secondary small text-uppercase mb-0">
                                            Tema Visual y Reglas:
                                        </label>
                                        <div className="d-flex gap-1">
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-primary py-0 px-2 small"
                                                onClick={handleOpenCreateTheme}
                                                title="Crear un nuevo tema visual"
                                                disabled={isBusy || matchPhase === 'RUNNING' || matchPhase === 'COUNTDOWN'}
                                            >
                                                <i className="bi bi-plus-lg me-1"></i> + Nuevo
                                            </button>
                                            {selectedThemeId && (
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-secondary py-0 px-2 small"
                                                    onClick={() => {
                                                        const current = themesList.find((t) => String(t.id) === String(selectedThemeId));
                                                        if (current) handleOpenEditTheme(current);
                                                    }}
                                                    title="Editar tema seleccionado"
                                                    disabled={isBusy || matchPhase === 'RUNNING' || matchPhase === 'COUNTDOWN'}
                                                >
                                                    <i className="bi bi-pencil-fill me-1"></i> Editar
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    <select
                                        className="form-select"
                                        value={selectedThemeId}
                                        onChange={(e) => handleThemeSelectionChange(e.target.value)}
                                        disabled={isBusy || matchPhase === 'RUNNING' || matchPhase === 'COUNTDOWN'}
                                    >
                                        <option value="" disabled>-- Selecciona un tema visual --</option>
                                        {themesList.map((t) => (
                                            <option key={t.id} value={t.id}>
                                                {t.name} {t.isSystemPreset ? '(Preset Oficial)' : '(Personalizado)'}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="row g-3">
                                    {/* DURACIÓN */}
                                    <div className="col-12 col-sm-6">
                                        <label className="form-label fw-bold text-secondary small text-uppercase">
                                            Duración de Partida:
                                        </label>
                                        <select
                                            className="form-select"
                                            value={durationSeconds}
                                            onChange={(e) => setDurationSeconds(Number(e.target.value))}
                                            disabled={isBusy || matchPhase === 'RUNNING' || matchPhase === 'COUNTDOWN'}
                                        >
                                            <option value={15}>15 Segundos (Flash)</option>
                                            <option value={30}>30 Segundos (Rápida)</option>
                                            <option value={45}>45 Segundos (Estándar)</option>
                                            <option value={60}>60 Segundos (Maratón)</option>
                                        </select>
                                    </div>

                                    {/* DISTANCIA */}
                                    <div className="col-12 col-sm-6">
                                        <label className="form-label fw-bold text-secondary small text-uppercase">
                                            Distancia Total:
                                        </label>
                                        <select
                                            className="form-select"
                                            value={totalKilometers}
                                            onChange={(e) => setTotalKilometers(Number(e.target.value))}
                                            disabled={isBusy || matchPhase === 'RUNNING' || matchPhase === 'COUNTDOWN'}
                                        >
                                            <option value={3}>3K (3 Kilómetros)</option>
                                            <option value={5}>5K (5 Kilómetros)</option>
                                            <option value={10}>10K (10 Kilómetros)</option>
                                            <option value={0}>Sin distancia (Libre)</option>
                                        </select>
                                    </div>
                                </div>

                                {/* SWITCH ARCOS DE ESPUMA */}
                                <div className="form-check form-switch mt-3 pt-2 border-top">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="foamArchesSwitch"
                                        checked={enableFoamArches}
                                        onChange={(e) => setEnableFoamArches(e.target.checked)}
                                        disabled={isBusy || matchPhase === 'RUNNING' || matchPhase === 'COUNTDOWN'}
                                    />
                                    <label className="form-check-label fw-bold small text-dark" htmlFor="foamArchesSwitch">
                                        Activar Arcos de Espuma patrocinados en hitos de Km
                                    </label>
                                </div>

                                <div className="mt-3 text-end">
                                    <button
                                        className="btn btn-outline-primary w-100"
                                        onClick={handlePrepareMatch}
                                        disabled={isBusy || matchPhase === 'RUNNING' || matchPhase === 'COUNTDOWN'}
                                    >
                                        <i className="bi bi-save me-1"></i> Aplicar y Preparar Sala
                                    </button>
                                </div>

                            </div>
                        </div>
                    </div>

                    {/* TARJETA 2: PROYECCIÓN Y ENLACES DE SALA */}
                    <div className="col-12 col-lg-6">
                        <div className="card h-100 shadow-sm border-0 rounded-3">
                            <div className="card-header bg-dark text-white d-flex justify-content-between align-items-center py-3">
                                <h5 className="mb-0 fw-bold">
                                    <i className="bi bi-tv-fill me-2 text-info"></i>
                                    2. Proyección y Enlaces de Sala
                                </h5>
                                <span className="badge bg-secondary">Lienzo V2</span>
                            </div>
                            <div className="card-body bg-light d-flex flex-column justify-content-between">
                                
                                <p className="text-muted small mb-3">
                                    Controla qué visualiza la pantalla gigante del estadio y gestiona el acceso de los aficionados.
                                </p>

                                <div className="d-grid gap-2">
                                    
                                    {/* BOTÓN MOSTRAR PORTADA */}
                                    <button
                                        className="btn btn-outline-dark py-2 d-flex align-items-center justify-content-center"
                                        onClick={handleShowCover}
                                        disabled={isBusy}
                                    >
                                        <i className="bi bi-image me-2"></i>
                                        Mostrar portada
                                    </button>

                                    {/* BOTÓN PROYECTAR PANTALLA DE JUEGO */}
                                    <button
                                        className="btn btn-outline-primary py-2 d-flex align-items-center justify-content-center"
                                        onClick={handleProjectGame}
                                        disabled={isBusy}
                                    >
                                        <i className="bi bi-display me-2"></i>
                                        Proyectar pantalla de juego
                                    </button>

                                    {/* BOTÓN INICIAR PARTIDA */}
                                    <button
                                        className="btn btn-success py-2 d-flex align-items-center justify-content-center"
                                        onClick={handleStartMatch}
                                        disabled={isBusy || matchPhase === 'RUNNING' || matchPhase === 'COUNTDOWN'}
                                    >
                                        <i className="bi bi-play-circle-fill me-2"></i>
                                        {matchPhase === 'RUNNING' ? 'Partida en curso...' : 'Iniciar partida'}
                                    </button>

                                    {/* BOTÓN REINICIAR PARTIDA CON CONFIRMACIÓN */}
                                    <button
                                        className="btn btn-outline-danger py-2 d-flex align-items-center justify-content-center"
                                        onClick={() => openConfirmModal('¿Estás seguro de que deseas reiniciar la partida? Todos los contadores y avances volverán a cero.', handleResetMatch, 'Sí, reiniciar')}
                                        disabled={isBusy}
                                    >
                                        <i className="bi bi-arrow-counterclockwise me-2"></i>
                                        Reiniciar partida
                                    </button>

                                    {/* BOTÓN VER QR */}
                                    <button
                                        className="btn btn-outline-primary py-2 d-flex align-items-center justify-content-center"
                                        onClick={() => setShowQRModal(true)}
                                    >
                                        <i className="bi bi-qr-code-scan me-2"></i> Instrucciones de conexión
                                    </button>

                                </div>

                            </div>
                        </div>
                    </div>

                </div>

                {/* FILA 2: TARJETA 3 (TELEMETRÍA ESPACIAL EN VIVO) */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="card shadow-sm border-0 rounded-3 overflow-hidden">
                            <div className="card-header bg-dark text-white d-flex justify-content-between align-items-center py-3">
                                <h5 className="mb-0 fw-bold">
                                    <i className="bi bi-speedometer2 me-2 text-success"></i>
                                    3. Telemetría de Carrera en Vivo
                                </h5>
                                <div className="d-flex align-items-center gap-2">
                                    {totalKilometers > 0 && (
                                        <span className="badge bg-light text-dark fw-bold">
                                            Km Líder: {liveTelemetry?.currentLeadingKilometer ?? 0} de {totalKilometers} Km
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="card-body p-4 bg-white">

                                {/* ANIMACIÓN DE CONTEO REGRESIVO */}
                                {matchPhase === 'COUNTDOWN' && (
                                    <div className="text-center py-4 my-2 bg-light rounded-3 border border-warning shadow-sm">
                                        <p className="text-muted fw-bold text-uppercase mb-1">¡Preparando arrancada sincronizada!</p>
                                        <h1 className="display-1 fw-black text-warning mb-0 animate__animated animate__pulse animate__infinite" style={{ fontSize: '5rem', fontWeight: 900 }}>
                                            {countdownNumber === 0 ? '¡A CORRER! 🏃💨' : countdownNumber}
                                        </h1>
                                    </div>
                                )}

                                {/* BARRA DE PROGRESO TEMPORAL GLOBAL */}
                                <div className="mb-4">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <span className="small fw-bold text-secondary text-uppercase">
                                            Avance de Carrera (Tiempo General):
                                        </span>
                                        <span className="fw-bold text-dark small">
                                            {liveTelemetry?.progressPercentage ?? 0}%
                                        </span>
                                    </div>
                                    <div className="progress" style={{ height: '14px', borderRadius: '8px' }}>
                                        <div
                                            className="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                                            role="progressbar"
                                            style={{ width: `${liveTelemetry?.progressPercentage ?? 0}%` }}
                                            aria-valuenow={liveTelemetry?.progressPercentage ?? 0}
                                            aria-valuemin="0"
                                            aria-valuemax="100"
                                        ></div>
                                    </div>
                                </div>

                                {/* BARRAS DE TELEMETRÍA POR ZONA */}
                                <h6 className="fw-bold text-dark text-uppercase small mb-3 border-bottom pb-2">
                                    Posición Espacial de Corredores por Zona:
                                </h6>

                                {(!liveTelemetry?.zonesProgress || liveTelemetry.zonesProgress.length === 0) ? (
                                    <div className="text-center py-4 bg-light rounded text-muted">
                                        <i className="bi bi-people fs-2 d-block mb-2 text-secondary"></i>
                                        Esperando telemetría de zonas activas...
                                    </div>
                                ) : (
                                    <div className="d-flex flex-column gap-3">
                                        {liveTelemetry.zonesProgress.map((zone, idx) => {
                                            const zoneColor = getZoneColor(zone.zoneIdentifier);
                                            return (
                                                <div key={zone.zoneIdentifier || idx} className="p-3 bg-light rounded-3 border">
                                                    <div className="d-flex flex-wrap justify-content-between align-items-center mb-2">
                                                        <div className="d-flex align-items-center">
                                                            <span
                                                                className="badge me-2 text-uppercase fw-bold px-3 py-2"
                                                                style={{ backgroundColor: zoneColor, color: '#fff' }}
                                                            >
                                                                Zona {zone.zoneIdentifier}
                                                            </span>
                                                            {idx === 0 && matchPhase === 'RUNNING' && (
                                                                <span className="badge bg-warning text-dark fw-bold">
                                                                    👑 Líder
                                                                </span>
                                                            )}
                                                        </div>

                                                        <div className="d-flex gap-3 small text-secondary">
                                                            <span><strong>{zone.totalTaps}</strong> taps totales</span>
                                                            <span>•</span>
                                                            <span><strong>{zone.activeParticipants}</strong> corredor(es)</span>
                                                            <span>•</span>
                                                            <span className="text-primary fw-bold">
                                                                {zone.scorePerCapita?.toFixed(2)} taps/cápita
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {/* BARRA DE PROGRESO DE LA ZONA */}
                                                    <div className="progress" style={{ height: '22px', borderRadius: '11px', backgroundColor: '#e9ecef' }}>
                                                        <div
                                                            className="progress-bar progress-bar-striped fw-bold"
                                                            role="progressbar"
                                                            style={{
                                                                width: `${Math.min(100, Math.max(2, zone.visualPositionPercentage || 0))}%`,
                                                                backgroundColor: zoneColor,
                                                                transition: 'width 0.1s linear'
                                                            }}
                                                        >
                                                            {zone.visualPositionPercentage ? `${zone.visualPositionPercentage.toFixed(1)}%` : '0%'}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {/* BANNER DE GANADOR SI FINALIZÓ */}
                                {matchPhase === 'FINISHED' && winnerResult && (
                                    <div className="mt-4 p-4 bg-success bg-opacity-10 border border-success rounded-3 text-center">
                                        <h3 className="fw-bold text-success mb-1">
                                            🏆 ¡Ganador de la Carrera!
                                        </h3>
                                        <p className="fs-4 fw-black text-dark text-uppercase mb-2">
                                            {winnerResult.isTie ? '¡Empate Total!' : `Zona ${winnerResult.winnerZone}`}
                                        </p>
                                    </div>
                                )}

                            </div>
                        </div>
                    </div>
                </div>

                {/* ========================================================================= */}
                {/* SECCIÓN DEDICADA: GESTOR DE TEMAS Y DISEÑOS VISUALES (HU-04) */}
                {/* ========================================================================= */}
                <div className="card shadow-sm border-0 rounded-3 mb-4 overflow-hidden">
                    <div className="card-header bg-dark text-white d-flex flex-wrap justify-content-between align-items-center py-3 px-4">
                        <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-palette-fill fs-4"></i>
                            <div>
                                <h5 className="mb-0 fw-bold">Temas y diseños de carrera</h5>
                            </div>
                        </div>

                        <div className="d-flex align-items-center gap-2 mt-2 mt-sm-0">
                            <span className="small px-3 py-2">
                                {themesList.length} Tema(s) Disponible(s)
                            </span>
                            <button
                                type="button"
                                className="btn btn-outline-success btn-sm fw-bold px-3 py-2 shadow-sm d-flex align-items-center gap-1"
                                onClick={handleOpenCreateTheme}
                                disabled={isBusy || matchPhase === 'RUNNING' || matchPhase === 'COUNTDOWN'}
                            >
                                <i className="bi bi-plus-lg fs-6"></i> Crear Nuevo Tema
                            </button>
                        </div>
                    </div>

                    <div className="card-body bg-light p-4">
                        {themesList.length === 0 ? (
                            <div className="text-center py-4 bg-white rounded-3 border">
                                <i className="bi bi-palette fs-1 text-muted mb-2 d-block"></i>
                                <p className="text-muted fw-semibold mb-2">No hay temas disponibles en este momento.</p>
                                <button
                                    type="button"
                                    className="btn btn-primary btn-sm fw-bold"
                                    onClick={handleOpenCreateTheme}
                                >
                                    <i className="bi bi-plus-lg me-1"></i> Crear mi primer tema
                                </button>
                            </div>
                        ) : (
                            <div className="row g-3">
                                {themesList.map((theme) => {
                                    const isSelected = String(selectedThemeId) === String(theme.id);
                                    const bgPreview = theme.coverBackgroundUrl || theme.trackBackgroundUrl || theme.skyBackgroundUrl;
                                    const cardPrimary = theme.primaryColor || '#0d6efd';
                                    const cardSecondary = theme.secondaryColor || '#ffc107';

                                    return (
                                        <div key={theme.id} className="col-12 col-md-6 col-xl-4">
                                            <div
                                                className={`card h-100 border-2 rounded-3 shadow-sm transition-all overflow-hidden bg-white ${
                                                    isSelected ? 'border-primary' : 'border-light-subtle'
                                                }`}
                                                style={{
                                                    boxShadow: isSelected ? '0 0 0 2px rgba(13, 110, 253, 0.35)' : undefined
                                                }}
                                            >
                                                {/* BANNER PREVIEW / HEADER DE LA TARJETA */}
                                                <div
                                                    className="position-relative d-flex align-items-end p-3"
                                                    style={{
                                                        height: '110px',
                                                        background: bgPreview
                                                            ? `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.7)), url(${getFullAssetUrl(bgPreview)}) center/cover no-repeat`
                                                            : `linear-gradient(135deg, ${cardPrimary}, ${cardSecondary})`
                                                    }}
                                                >
                                                    {/* BADGES EN CABECERA */}
                                                    <div className="position-absolute top-0 start-0 p-2 d-flex gap-1 flex-wrap">
                                                        {theme.isSystemPreset ? (
                                                            <span className="badge bg-dark bg-opacity-75 border border-light border-opacity-25 small">
                                                                <i className="bi bi-shield-check me-1 text-warning"></i>
                                                                Preset Oficial
                                                            </span>
                                                        ) : (
                                                            <span className="badge bg-secondary bg-opacity-75 border border-light border-opacity-25 small">
                                                                <i className="bi bi-person me-1"></i>
                                                                Personalizado
                                                            </span>
                                                        )}

                                                        <span className="badge bg-info bg-opacity-75 text-dark fw-bold small">
                                                            {theme.totalKilometers > 0 ? `${theme.totalKilometers}K` : 'Libre'}
                                                        </span>
                                                    </div>

                                                    {/* BADGE ACTIVO */}
                                                    {isSelected && (
                                                        <div className="position-absolute top-0 end-0 p-2">
                                                            <span className="badge bg-success shadow-sm px-2 py-1 fw-bold">
                                                                <i className="bi bi-check-circle-fill me-1"></i>
                                                                ACTIVO
                                                            </span>
                                                        </div>
                                                    )}

                                                    {/* LOGO DEL CLUB / MARCA SI EXISTE */}
                                                    {theme.brandLogoUrl && (
                                                        <div className="position-absolute bottom-0 end-0 p-2">
                                                            <img
                                                                src={getFullAssetUrl(theme.brandLogoUrl)}
                                                                alt="Club logo"
                                                                className="rounded bg-white bg-opacity-75 p-1 shadow-sm"
                                                                style={{ maxHeight: '35px', maxWidth: '60px', objectFit: 'contain' }}
                                                            />
                                                        </div>
                                                    )}

                                                    <h6 className="text-white fw-bold text-truncate mb-0" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.8)' }} title={theme.name}>
                                                        {theme.name}
                                                    </h6>
                                                </div>

                                                {/* CONTENIDO DE LA TARJETA */}
                                                <div className="card-body p-3 d-flex flex-column justify-content-between">
                                                    <div className="d-flex flex-wrap gap-2 align-items-center mb-3 small text-secondary">
                                                        {/* SWATCH DE COLORES */}
                                                        <div className="d-flex align-items-center gap-1">
                                                            <span className="small">Colores:</span>
                                                            <span
                                                                className="rounded-circle border"
                                                                style={{ width: '16px', height: '16px', backgroundColor: cardPrimary }}
                                                                title={`Primario: ${cardPrimary}`}
                                                            />
                                                            <span
                                                                className="rounded-circle border"
                                                                style={{ width: '16px', height: '16px', backgroundColor: cardSecondary }}
                                                                title={`Secundario: ${cardSecondary}`}
                                                            />
                                                        </div>

                                                        <span>•</span>

                                                        {/* ESPUMA */}
                                                        <span>
                                                            <i className={`bi ${theme.enableFoamArches ? 'bi-check-circle-fill text-success' : 'bi-x-circle text-muted'} me-1`}></i>
                                                            {theme.enableFoamArches ? 'Arcos Espuma' : 'Sin Espuma'}
                                                        </span>
                                                    </div>

                                                    {/* BOTONERA DE ACCIÓN */}
                                                    <div className="d-flex gap-2 align-items-center">
                                                        {isSelected ? (
                                                            <button
                                                                type="button"
                                                                className="btn btn-success btn-sm fw-bold flex-grow-1"
                                                                disabled
                                                            >
                                                                <i className="bi bi-check2-all me-1"></i> Seleccionado
                                                            </button>
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-primary btn-sm fw-bold flex-grow-1"
                                                                onClick={() => handleThemeSelectionChange(theme.id)}
                                                                disabled={isBusy || matchPhase === 'RUNNING' || matchPhase === 'COUNTDOWN'}
                                                            >
                                                                Seleccionar
                                                            </button>
                                                        )}

                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-secondary btn-sm"
                                                            onClick={() => handleOpenEditTheme(theme)}
                                                            title="Editar tema"
                                                            disabled={isBusy || matchPhase === 'RUNNING' || matchPhase === 'COUNTDOWN'}
                                                        >
                                                            <i className="bi bi-pencil-fill"></i>
                                                        </button>

                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-info btn-sm"
                                                            onClick={() => handleCloneTheme(theme)}
                                                            title="Clonar como copia editable"
                                                            disabled={isBusy}
                                                        >
                                                            <i className="bi bi-copy"></i>
                                                        </button>

                                                        {!theme.isSystemPreset && (
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-danger btn-sm"
                                                                onClick={() => handleDeleteTheme(theme)}
                                                                title="Eliminar tema"
                                                                disabled={isBusy || matchPhase === 'RUNNING' || matchPhase === 'COUNTDOWN'}
                                                            >
                                                                <i className="bi bi-trash-fill"></i>
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                {/* FILA 3: TARJETA 4 (CONTROLES DE EMERGENCIA) Y TARJETA 5 (HISTORIAL) */}
                <div className="row g-4">
                    
                    {/* TARJETA 4: CONTROLES DE EMERGENCIA */}
                    <div className="col-12 col-lg-5">
                        <div className="card h-100 shadow-sm border-0 rounded-3">
                            <div className="card-header bg-danger text-white py-3">
                                <h5 className="mb-0 fw-bold">
                                    <i className="bi bi-exclamation-triangle-fill me-2"></i> Controles de emergencia
                                </h5>
                            </div>
                            <div className="card-body bg-light d-flex flex-column justify-content-between">
                                <p className="text-muted small mb-3">
                                    Acciones de interrupción y control manual durante la transmisión en vivo.
                                </p>

                                <div className="d-grid gap-2">
                                    
                                    {matchPhase === 'RUNNING' ? (
                                        <button
                                            className="btn btn-warning fw-bold py-2"
                                            onClick={handlePauseMatch}
                                            disabled={isBusy}
                                        >
                                            <i className="bi bi-pause-fill me-2 fs-5"></i>
                                            Pausar Carrera
                                        </button>
                                    ) : (
                                        <button
                                            className="btn btn-outline-success fw-bold py-2"
                                            onClick={handleResumeMatch}
                                            disabled={isBusy || matchPhase !== 'PAUSED'}
                                        >
                                            <i className="bi bi-play-fill me-2 fs-5"></i>
                                            Reanudar Carrera
                                        </button>
                                    )}

                                    {/* <button
                                        className="btn btn-outline-danger fw-bold py-2"
                                        onClick={handleFinishEarly}
                                        disabled={isBusy || matchPhase === 'WAITING' || matchPhase === 'FINISHED'}
                                    >
                                        <i className="bi bi-stop-circle-fill me-2 fs-5"></i>
                                        Finalizar Anticipadamente
                                    </button>

                                    <button
                                        className="btn btn-danger fw-bold py-2"
                                        onClick={handleResetMatch}
                                        disabled={isBusy}
                                    >
                                        <i className="bi bi-arrow-counterclockwise me-2 fs-5"></i>
                                        Reiniciar Carrera a Espera
                                    </button> */}

                                </div>
                            </div>
                        </div>
                    </div>

                    {/* TARJETA 5: HISTORIAL DE PARTIDAS */}
                    <div className="col-12 col-lg-7">
                        <div className="card h-100 shadow-sm border-0 rounded-3">
                            <div className="card-header bg-dark text-white d-flex justify-content-between align-items-center py-3">
                                <h5 className="mb-0 fw-bold">
                                    <i className="bi bi-clock-history me-2 text-warning"></i> Historial de partidas
                                </h5>
                                <button
                                    className="btn btn-sm btn-outline-light"
                                    onClick={loadHistory}
                                    disabled={isLoadingHistory}
                                    title="Actualizar historial"
                                >
                                    <i className={`bi bi-arrow-clockwise ${isLoadingHistory ? 'spin' : ''}`}></i>
                                </button>
                            </div>
                            <div className="card-body p-0 bg-white">
                                
                                <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
                                    {matchHistory.length === 0 ? (
                                        <div className="text-center py-4 text-muted small">
                                            No hay partidas registradas en esta sala aún.
                                        </div>
                                    ) : (
                                        <table className="table table-hover table-striped mb-0 small">
                                            <thead className="table-light sticky-top">
                                                <tr>
                                                    <th>Fecha</th>
                                                    <th>Tema</th>
                                                    <th>Duración</th>
                                                    <th>Distancia</th>
                                                    <th>Ganador</th>
                                                    <th>Participantes</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {matchHistory.map((m) => (
                                                    <tr key={m.id}>
                                                        <td>
                                                            {m.playedAt ? new Date(m.playedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}
                                                        </td>
                                                        <td className="fw-semibold text-truncate" style={{ maxWidth: '120px' }}>
                                                            {m.themeName || 'Estándar'}
                                                        </td>
                                                        <td>{m.durationSeconds}s</td>
                                                        <td>{m.totalKilometers > 0 ? `${m.totalKilometers}K` : 'Libre'}</td>
                                                        <td>
                                                            <span className="badge bg-success text-uppercase">
                                                                {m.winnerZone || 'Empate'}
                                                            </span>
                                                        </td>
                                                        <td className="fw-bold">{m.totalParticipants || 0}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>

                            </div>
                        </div>
                    </div>

                </div>

            </div>

            {/* ========================================================================= */}
            {/* MODAL DE GESTIÓN DE TEMAS (HU-04) */}
            {/* ========================================================================= */}
            {showThemeModal && (
                <CarreraContinuaThemeModal
                    show={showThemeModal}
                    onClose={() => setShowThemeModal(false)}
                    onSave={handleThemeSaved}
                    themeToEdit={themeToEdit}
                    roomId={roomId}
                    operatorId={user?.id}
                />
            )}

            {/* ========================================================================= */}
            {/* MODAL DE INSTRUCCIONES Y CÓDIGO QR (UX ESTRICTA Z-INDEX) */}
            {/* ========================================================================= */}
            {showQRModal && (
                <>
                    {/* BACKDROP CON Z-INDEX: 99998 */}
                    <div
                        onClick={() => setShowQRModal(false)}
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            width: '100vw',
                            height: '100vh',
                            backgroundColor: 'rgba(0, 0, 0, 0.75)',
                            zIndex: 99998,
                            backdropFilter: 'blur(3px)'
                        }}
                    />

                    {/* MODAL DIALOG CON Z-INDEX: 99999 */}
                    <div
                        style={{
                            position: 'fixed',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            maxWidth: '440px',
                            width: '90%',
                            zIndex: 99999
                        }}
                    >
                        <div className="card border-0 shadow-lg rounded-4 overflow-hidden">
                            <div className="card-header bg-dark text-white d-flex justify-content-between align-items-center py-3 px-4">
                                <h5 className="mb-0 fw-bold">
                                    <i className="bi bi-qr-code-scan me-2 text-primary"></i>
                                    ¡Únete a la Carrera!
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    aria-label="Cerrar"
                                    onClick={() => setShowQRModal(false)}
                                />
                            </div>

                            <div className="card-body p-4 text-center bg-white">
                                <p className="text-muted small mb-3">
                                    Escanea el código QR con la cámara de tu celular para unirte a la zona de carrera de tu grada:
                                </p>

                                {/* CANVAS DEL QR CON QRCODECANVAS Y LOGO DE MARCA/CLUB */}
                                <div className="d-inline-block p-3 bg-light rounded-3 border shadow-sm mb-3">
                                    <QRCodeCanvas
                                        value={playerUrl}
                                        size={220}
                                        level="H"
                                        includeMargin={true}
                                        imageSettings={{
                                            src: (themesList.find(t => String(t.id) === String(selectedThemeId))?.brandLogoUrl ? getFullAssetUrl(themesList.find(t => String(t.id) === String(selectedThemeId)).brandLogoUrl) : '/img/page/xolos-five-bicolor.png'),
                                            x: undefined,
                                            y: undefined,
                                            height: 44,
                                            width: 44,
                                            excavate: true
                                        }}
                                    />
                                </div>

                                {/* INPUT DE COPIA RÁPIDA */}
                                <div className="input-group mb-3">
                                    <input
                                        type="text"
                                        className="form-control form-control-sm text-center bg-light user-select-all"
                                        value={playerUrl}
                                        readOnly
                                    />
                                    <button
                                        className={`btn btn-sm ${isCopied ? 'btn-success' : 'btn-outline-secondary'}`}
                                        type="button"
                                        onClick={handleCopyUrl}
                                    >
                                        <i className={`bi ${isCopied ? 'bi-check2' : 'bi-clipboard'} me-1`}></i>
                                        {isCopied ? 'Copiado' : 'Copiar'}
                                    </button>
                                </div>

                                {/* BOTÓN ABRIR EN NUEVA PESTAÑA */}
                                <button
                                    className="btn btn-primary w-100 fw-bold mb-2 py-2"
                                    onClick={() => window.open(playerUrl, '_blank')}
                                >
                                    <i className="bi bi-box-arrow-up-right me-2"></i>
                                    Abrir jugador en nueva pestaña
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* ========================================================================= */}
            {/* MODAL DE CONFIRMACIÓN */}
            {/* ========================================================================= */}
            {showConfirmModal && (
                <Confirm
                    message={confirmModalData?.message}
                    actionToYesButton={confirmModalData?.actionToYesButton}
                    textYesButton={confirmModalData?.textYesButton}
                    textNoButton={confirmModalData?.textNoButton}
                    setShowModal={setShowConfirmModal}
                    setConfirmData={setConfirmModalData}
                />
            )}

        </SecondaryBase>
    );
};

export { CarreraContinua };
